<script setup>
import { computed } from 'vue'
import { Link } from '@inertiajs/vue3'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import SettingsTabs from '@/Components/UI/SettingsTabs.vue'
import UserCard from '@/Components/Cards/UserCard.vue'
import { useCurrency } from '@/composables/useCurrency.js'
import { setPageHeaderCount } from '@/composables/usePageHeader.js'

defineOptions({ layout: AppLayout })

const props = defineProps({
  users: { type: Array, required: true },
})

const roleStyle = (role) => ({
  admin:  { background: 'var(--infoSoft)',   color: 'var(--info)'   },
  staff:  { background: 'var(--okSoft)',     color: 'var(--ok)'     },
  viewer: { background: 'var(--accentSoft)', color: 'var(--accent)' },
}[role] || { background: 'var(--surfaceAlt)', color: 'var(--inkSoft)' })

const { formatDate } = useCurrency()

setPageHeaderCount(computed(() => props.users?.length ?? 0), () => 'users')
</script>

<template>
  <PageShell title="Users">
    <SettingsTabs />

    <div class="flex justify-end -mt-2">
      <Link href="/users/new">
        <UiButton
          variant="primary"
          icon-left="plus"
        >
          Add user
        </UiButton>
      </Link>
    </div>

    <!-- Mobile: card stack -->
    <div class="md:hidden space-y-2">
      <div
        v-if="!users.length"
        class="px-6 py-10 text-center text-[color:var(--muted)]"
      >
        No users yet.
      </div>
      <Link
        v-for="user in users"
        :key="user.id"
        :href="`/users/${user.id}/edit`"
        class="block rounded-xl border border-[color:var(--line)] bg-[color:var(--surface)] p-3 active:bg-[color:var(--surfaceAlt)]"
      >
        <UserCard :user="user" />
      </Link>
    </div>

    <!-- md+: existing table (wrapped) -->
    <div
      class="hidden md:block rounded-xl overflow-hidden"
      :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
    >
      <table class="w-full text-sm">
        <thead>
          <tr :style="{ background: 'var(--surfaceAlt)', borderBottom: '1px solid var(--line)' }">
            <th class="px-6 py-3 text-left font-semibold text-[color:var(--muted)] uppercase tracking-wider text-xs">
              Name
            </th>
            <th class="px-6 py-3 text-left font-semibold text-[color:var(--muted)] uppercase tracking-wider text-xs">
              Email
            </th>
            <th class="px-6 py-3 text-left font-semibold text-[color:var(--muted)] uppercase tracking-wider text-xs">
              Role
            </th>
            <th class="hidden md:table-cell px-6 py-3 text-left font-semibold text-[color:var(--muted)] uppercase tracking-wider text-xs">
              Joined
            </th>
            <th class="px-6 py-3" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(user, i) in users"
            :key="user.id"
            class="transition-colors hover:bg-[var(--surfaceAlt)]"
            :style="{ borderTop: i === 0 ? 'none' : '1px solid var(--line)' }"
          >
            <td class="px-6 py-4 font-medium text-[color:var(--ink)]">
              {{ user.full_name }}
            </td>
            <td class="px-6 py-4 text-[color:var(--inkSoft)]">
              {{ user.email }}
            </td>
            <td class="px-6 py-4">
              <span
                class="inline-block text-xs px-2 py-0.5 rounded-full font-medium capitalize"
                :style="roleStyle(user.role)"
              >{{ user.role }}</span>
            </td>
            <td class="hidden md:table-cell px-6 py-4 text-[color:var(--muted)]">
              {{ formatDate(user.created_at) }}
            </td>
            <td class="px-6 py-4 text-right space-x-3">
              <Link
                :href="`/users/${user.id}/edit`"
                class="text-[color:var(--accent)] hover:underline"
              >
                Edit
              </Link>
              <Link
                :href="`/users/${user.id}`"
                method="delete"
                as="button"
                class="text-[color:var(--danger)] hover:underline"
                onclick="return confirm('Remove this user?')"
              >
                Remove
              </Link>
            </td>
          </tr>
          <tr v-if="!users.length">
            <td
              colspan="5"
              class="px-6 py-10 text-center text-[color:var(--muted)]"
            >
              No users yet.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </PageShell>
</template>
